import type { AdminOrganizationDetail } from '../../../../domain/superadmin/types';
import { formatPhone } from '../../../../utils/formatters';
import DashboardCard from '../../../dashboard/components/DashboardCard';
import { cn } from '@/lib/utils';
import { SectionEmpty } from './SectionEmpty';

type ChannelsSectionProps = {
  channels: AdminOrganizationDetail['channels'];
  whatsappInstances: AdminOrganizationDetail['whatsappInstances'];
};

const formatLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

function connectionStatusClass(status: string | null, bucket?: string) {
  const key = (bucket ?? status ?? '').toLowerCase();
  if (key === 'connected' || key === 'active') {
    return 'border-success/30 bg-success-bg text-success-text';
  }
  if (key === 'pending' || key === 'connecting') {
    return 'border-warning/30 bg-warning-bg text-warning-text';
  }
  return 'border-border bg-surface-subtle text-muted';
}

function ChannelRow({
  name,
  type,
  status,
  statusBucket,
  detail,
}: {
  name: string;
  type: string;
  status: string | null;
  statusBucket?: string;
  detail?: string | null;
}) {
  return (
    <li className="flex flex-col gap-1 rounded-lg border border-subtle bg-surface px-s4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium text-default">{name}</p>
        <p className="text-[11.5px] text-muted">
          {formatLabel(type)}
          {detail ? ` · ${detail}` : ''}
        </p>
      </div>
      <span
        className={cn(
          'inline-flex w-fit shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium',
          connectionStatusClass(status, statusBucket),
        )}
      >
        {status ? formatLabel(status) : '—'}
      </span>
    </li>
  );
}

export function ChannelsSection({ channels, whatsappInstances }: ChannelsSectionProps) {
  const hasChannels = channels.length > 0;
  const hasWhatsapp = whatsappInstances.length > 0;

  if (!hasChannels && !hasWhatsapp) {
    return (
      <DashboardCard title="Canais e WhatsApp" className="mb-s5">
        <SectionEmpty message="Nenhum canal ou instância WhatsApp configurada." />
      </DashboardCard>
    );
  }

  return (
    <div className="mb-s5 grid gap-s5 lg:grid-cols-2">
      <DashboardCard title="Canais">
        {!hasChannels ? (
          <SectionEmpty message="Nenhuma integração de canal encontrada." />
        ) : (
          <ul className="flex flex-col gap-s2">
            {channels.map((channel) => (
              <ChannelRow
                key={channel.id}
                name={channel.name}
                type={channel.type}
                status={channel.status}
              />
            ))}
          </ul>
        )}
      </DashboardCard>

      <DashboardCard title="Instâncias WhatsApp">
        {!hasWhatsapp ? (
          <SectionEmpty message="Nenhuma instância WhatsApp encontrada." />
        ) : (
          <ul className="flex flex-col gap-s2">
            {whatsappInstances.map((instance) => (
              <ChannelRow
                key={instance.id}
                name={instance.instanceName ?? 'Instância WhatsApp'}
                type="whatsapp"
                status={instance.status}
                statusBucket={instance.statusBucket}
                detail={
                  instance.phoneNumber
                    ? formatPhone(instance.phoneNumber)
                    : null
                }
              />
            ))}
          </ul>
        )}
      </DashboardCard>
    </div>
  );
}
